export interface ConfiguratorValue {
  value: string;
  labelKey: string;
  label?: string;
  description?: string;
  info?: string;
  imageUrl?: string;
  selectable: boolean;
  standard?: boolean;
}

export interface ConfiguratorCstic {
  key: string;
  name: string;
  labelKey: string;
  label?: string;
  description?: string;
  summaryLabel: string;
  info?: string;
  imageUrl?: string;
  value: string | null;
  values: readonly ConfiguratorValue[];
  visible: boolean;
  readOnly: boolean;
  unit?: string;
}

export interface ConfiguratorInstance {
  id: string;
  parentId?: string;
  kind: "main" | "sub";
  kmatType: string;
  kmatIndex: number;
  cstics: Readonly<Record<string, ConfiguratorCstic>>;
}

export interface ConfiguratorConflictCstic {
  key: string;
  name: string;
}

export interface ConfiguratorConflict {
  id: string;
  cstics: readonly ConfiguratorConflictCstic[];
}

export interface ConfiguratorAttachment {
  fileName: string;
  url: string;
  fileSizeBytes: number;
  uploadedAt: string | number;
}

/**
 * Normalized, backend-confirmed state consumed by the timeline engine.
 *
 * The API adapter is deliberately outside this model. V1 DTO shape and array-based CSTIC lookup
 * must not leak into v2 definitions or renderers.
 */
export interface ConfiguratorConfiguration {
  configId: string;
  productCode: string;
  priceFingerprint: string | null;
  availabilityFingerprint: string | null;
  productShortDesignation: string;
  description?: string;
  attachment?: ConfiguratorAttachment;
  helpUrl?: string | null;
  summaryImageUrls: readonly string[];
  instances: readonly ConfiguratorInstance[];
  conflicts: readonly ConfiguratorConflict[];
  currentStepNumber: number;
  highestVisitedStepNumber: number;
}

export interface ConfiguratorInstanceContext {
  instance: ConfiguratorInstance;
  mainInstance: ConfiguratorInstance;
  logicalIndex: number;
  instanceCount: number;
}

export const getMainInstance = (configuration: ConfiguratorConfiguration): ConfiguratorInstance => {
  const mainInstances = configuration.instances.filter((instance) => instance.kind === "main");

  if (mainInstances.length !== 1) {
    throw new Error(`Expected exactly one main configurator instance, received ${mainInstances.length}.`);
  }

  return mainInstances[0];
};
